import React, { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { ProfileRefreshModal } from "../../modals";
import {
  authIsAuthenticated,
  authUser,
} from "../../redux/selectors/authSelectors";
import {
  clearPromptSaveError,
  dismissPrompt,
  initFetchPromptStatus,
  initLogPrompt,
  markPromptShownLogged,
  setPromptSaveError,
  setPromptSaving,
} from "../../redux/actions/promptActions";
import { initSetProfile } from "../../redux/actions";

const getProfileRefreshErrorMessage = (error) => {
  if (!error) {
    return "";
  }

  if (typeof error.detail === "string") {
    return error.detail;
  }

  if (typeof error === "string") {
    return error;
  }

  const firstValue = Object.values(error)[0];

  if (Array.isArray(firstValue) && firstValue[0]) {
    return String(firstValue[0]);
  }

  if (typeof firstValue === "string") {
    return firstValue;
  }

  return "We could not save your update right now.";
};

const mapPromptVariant = (promptType) => {
  if (promptType === "six_month") {
    return "six-month";
  }
  if (promptType === "monthly") {
    return "monthly";
  }
  return "";
};

const PROMPT_DISPLAY_DELAY_MS = 10000;

const ProfilePromptShell = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const isAuth = useSelector((state) => authIsAuthenticated(state));
  const storageUser = useSelector((state) => authUser(state));
  const profile = useSelector((state) => state.profileReducer?.profile);
  const profileUpdateError = useSelector((state) => state.profileReducer?.error);
  const setProfileSuccess = useSelector((state) => state.profileReducer?.setProfileSuccess);
  const {
    promptType,
    certainty,
    careerAspirations,
    fetchAttempted,
    dismissed,
    shownLogged,
    isSaving,
    saveError,
  } = useSelector((state) => state.promptReducer);

  const [draft, setDraft] = useState({
    careers: "",
    certainty: 0,
  });
  const [isDelayComplete, setIsDelayComplete] = useState(false);
  const pendingSaveRef = useRef(false);

  const role = storageUser?.role;
  const user = profile || storageUser;
  const variant = mapPromptVariant(promptType);
  const shouldShowPrompt =
    isAuth &&
    role === "Student" &&
    fetchAttempted &&
    !dismissed &&
    (promptType === "monthly" || promptType === "six_month");
  const isOpen = shouldShowPrompt && isDelayComplete;

  useEffect(() => {
    if (!shouldShowPrompt) {
      setIsDelayComplete(false);
      return undefined;
    }

    const timer = window.setTimeout(() => {
      setIsDelayComplete(true);
    }, PROMPT_DISPLAY_DELAY_MS);

    return () => {
      window.clearTimeout(timer);
    };
  }, [shouldShowPrompt]);

  useEffect(() => {
    if (isAuth && role === "Student" && !fetchAttempted) {
      dispatch(initFetchPromptStatus());
    }
  }, [dispatch, fetchAttempted, isAuth, role]);

  useEffect(() => {
    setDraft({
      careers: careerAspirations || user?.careers || "",
      certainty: Number(certainty ?? user?.certainty) || 0,
    });
  }, [careerAspirations, certainty, user?.careers, user?.certainty]);

  useEffect(() => {
    if (!isOpen || shownLogged || !promptType) {
      return;
    }

    dispatch(
      initLogPrompt({
        prompt_type: promptType,
        action: "shown",
      })
    );
    dispatch(markPromptShownLogged());
  }, [dispatch, isOpen, promptType, shownLogged]);

  useEffect(() => {
    if (!pendingSaveRef.current || !setProfileSuccess) {
      return;
    }

    pendingSaveRef.current = false;
    dispatch(setPromptSaving(false));
    if (promptType) {
      dispatch(
        initLogPrompt({
          prompt_type: promptType,
          action: "confirmed",
        })
      );
    }
    dispatch(dismissPrompt());
    dispatch(clearPromptSaveError());
  }, [dispatch, promptType, setProfileSuccess]);

  useEffect(() => {
    if (!pendingSaveRef.current || isSaving) {
      return;
    }

    if (profileUpdateError) {
      pendingSaveRef.current = false;
      dispatch(
        setPromptSaveError(getProfileRefreshErrorMessage(profileUpdateError))
      );
    }
  }, [dispatch, isSaving, profileUpdateError]);

  const logAction = (action) => {
    if (!promptType) {
      return;
    }

    dispatch(
      initLogPrompt({
        prompt_type: promptType,
        action,
      })
    );
  };

  const closePrompt = () => {
    dispatch(dismissPrompt());
    dispatch(clearPromptSaveError());
  };

  const handleDraftChange = (field, value) => {
    dispatch(clearPromptSaveError());
    setDraft((prev) => ({
      ...prev,
      [field]: field === "certainty" ? Number(value) : value,
    }));
  };

  const handleLooksGood = () => {
    logAction("confirmed");
    closePrompt();
  };

  const handleRemindLater = () => {
    if (promptType === "monthly") {
      logAction("snoozed");
    }
    closePrompt();
  };

  const handleSave = () => {
    if (!user) {
      return;
    }

    const cleanedCareers = draft.careers.trim();
    const currentCareers = (user?.careers || "").trim();
    const currentCertainty = Number(user?.certainty) || 0;
    const nextCertainty = Number(draft.certainty) || 0;

    if (cleanedCareers === currentCareers && currentCertainty === nextCertainty) {
      handleLooksGood();
      return;
    }

    const payload = { ...user };
    delete payload.affiliate_code;
    delete payload.profile_image;

    pendingSaveRef.current = true;
    dispatch(setPromptSaving(true));
    dispatch(
      initSetProfile({
        ...payload,
        careers: cleanedCareers,
        certainty: nextCertainty || null,
      })
    );
  };

  const handlePrimaryAction = () => {
    if (promptType === "monthly") {
      handleSave();
      return;
    }

    logAction("review_intent");
    closePrompt();
    navigate("/profile/EditProfile");
  };

  if (!isOpen) {
    return null;
  }

  return (
    <ProfileRefreshModal
      careers={draft.careers}
      certainty={draft.certainty}
      isOpen={isOpen}
      isSaving={isSaving}
      onCareersChange={(value) => handleDraftChange("careers", value)}
      onCertaintyChange={(value) => handleDraftChange("certainty", value)}
      onClose={handleRemindLater}
      onConfirm={handleLooksGood}
      onPrimaryAction={handlePrimaryAction}
      saveError={saveError}
      user={user}
      variant={variant}
    />
  );
};

export default ProfilePromptShell;
